import { useEffect, useState } from "react";
import { Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";

import { getFreightModes } from "api";
import { GetFreightModesResponseType } from "api/response";
import errorToast from "helpers/errorToast";
import { getFreightModeIcon } from "helpers/freightModeHelper";
import usePermissions from "hooks/usePermissions";
import { IPaymentLocation } from "pages/common/Analytics/components/Reports/report";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import { useFormContext } from "react-hook-form";
import Button from "components/atoms/Button/Button";

function FreightModeField({ minSelected = 1 }: { minSelected?: number }) {
  const { permissions } = usePermissions();
  const permissionsData = permissions && JSON.parse(permissions);
  const { user_type } = permissionsData;
  const locations = permissionsData.payment_location?.locations;
  const intl = useIntl();
  const {
    setValue,
    watch,
    register,
    trigger,
    clearErrors,
    getValues,
    formState: { errors },
  } = useFormContext();
  const selectedFreightModes = watch("freight_modes") || [];

  const [freightModes, setFreightModes] = useState<GetFreightModesResponseType>([]);
  const [paymentLocations] = useState<IPaymentLocation[]>(locations);

  useEffect(() => {
    if (!freightModes?.length && (user_type === "provider" || paymentLocations.some((location) => location.is_active))) {
      fetchFreightModes();
    }
  }, []);

  useEffect(() => {
    register("freight_modes", { validate: validateMinSelected });
  }, [freightModes]);

  const fetchFreightModes = () => {
    const params = user_type === "provider" ? {} : buildLocationParams();
    getFreightModes({ params })
      .then((res) => setFreightModes(res.data))
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "general.couldNotRetrieveFreightModes",
            defaultMessage: "Could not retrieve freight modes. Please try again later.",
          }),
        ),
      );
  };

  const buildLocationParams = () => {
    const locationsString = paymentLocations
      .filter((location: any) => location.is_active === true)
      .map((location: any) => location.value)
      .join(",");
    return { locations: locationsString };
  };

  const handleSelect = (freightMode: any) => {
    const updatedFreightModes = selectedFreightModes.includes(freightMode.key)
      ? selectedFreightModes.filter((mode: any) => mode !== freightMode.key)
      : [...selectedFreightModes, freightMode.key];
    setValue("freight_modes", updatedFreightModes);
    trigger("freight_modes");
  };

  const validateMinSelected = () => {
    if (minSelected !== 1) return true;
    const selectedCount = getValues("freight_modes")?.length || 0;
    if (selectedCount >= minSelected) clearErrors("freight_modes"); // Clear errors if minimum selected is met
    return selectedCount >= minSelected;
  };

  const handleToggleAll = (isChecked: boolean) => {
    setValue("freight_modes", isChecked ? freightModes.map((mode: any) => mode.key) : []);
    trigger("freight_modes");
  };

  return (
    <div className="request-row d-flex">
      <Col xs={3}>
        <div className="font-weight-bold font-b1 mb-1">
          <FormattedMessage id="report.freightModes" defaultMessage="Freight modes" />
        </div>
        <div className="font-b3 text-secondary">
          <FormattedMessage id="report.chooseFreightModes" defaultMessage="Choose freight modes" />
        </div>
      </Col>
      {freightModes?.length > 0 ? (
        <Col xs={9} style={{ maxWidth: "384px" }}>
          <div className="freight-mode-filter justify-content-start">
            {freightModes.map((freightMode: any) => {
              const isSelected = selectedFreightModes?.includes(freightMode.key);
              const Icon = getFreightModeIcon(freightMode.value, freightMode.freight_mode_type, !isSelected);
              return (
                <OverlayTrigger
                  key={freightMode.key}
                  placement="top"
                  overlay={
                    <Tooltip id="freight-mode" style={{ zIndex: 999999 }}>
                      <span className="font-weight-normal">{freightMode?.translated_value ?? freightMode.value}</span>
                    </Tooltip>
                  }
                >
                  <button
                    type="button"
                    className={`freight-mode-item ${isSelected ? "active" : ""}`}
                    onClick={() => handleSelect(freightMode)}
                  >
                    {Icon && <Icon />}
                  </button>
                </OverlayTrigger>
              );
            })}
            {errors.freight_modes && (
              <div className="d-block font-b3 invalid-feedback">
                <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />
              </div>
            )}
          </div>
          <div className="d-flex">
            <Button onClick={() => handleToggleAll(true)} variant="outline-primary">
              <FormattedMessage id="general.selectAll" defaultMessage="Select all" />
            </Button>
            {selectedFreightModes.length > 0 && (
              <Button onClick={() => handleToggleAll(false)} variant="outline-primary" className="ml-3">
                <FormattedMessage id="general.clearAll" defaultMessage="Clear all" />
              </Button>
            )}
          </div>
        </Col>
      ) : (
        <Col sm={4}>
          <LoadingSkeleton rows={1} />
        </Col>
      )}
    </div>
  );
}

FreightModeField.defaultProps = {
  minSelected: 0,
};

export default FreightModeField;
